/**
 * Un déploiement ne doit pas casser l'onglet resté ouvert — [DECISIONS §309].
 *
 * **Vu en production le 21 août, deux minutes après une mise en ligne.** Un
 * candidat naviguait ; sa page demande `/assets/ParcoursScreen-Dgh6mJh-.js`, un
 * morceau chargé à la demande. Le déploiement venait de remplacer le paquet, et
 * ce nom-là n'existait plus : 404. L'écran Parcours ne s'ouvre pas, et rien ne
 * le lui dit — React voit une promesse rejetée, pas une version périmée.
 *
 * Un seul cas ce jour-là. Mais c'est la mécanique qui compte : cela se produit
 * pour CHAQUE onglet ouvert à CHAQUE déploiement, et nous déployons plusieurs
 * fois par jour à trois semaines du concours.
 *
 * **Recharger, plutôt qu'afficher une erreur.** La page suivante est la bonne :
 * l'index sert le nouveau paquet, et le candidat retombe où il était. Lui
 * demander de rafraîchir, c'est lui demander de comprendre un problème qui ne
 * le regarde pas.
 *
 * **Une seule fois, et c'est la partie qui compte vraiment.** Si le 404 vient
 * d'autre chose qu'une version périmée — un fichier réellement absent du
 * paquet —, recharger en boucle transformerait un écran cassé en navigateur
 * bloqué. Le drapeau vit dans `sessionStorage` : il dure le temps de l'onglet,
 * ce qui est exactement la durée du problème.
 *
 * ── LE CAS QUE §309 NE COUVRAIT PAS — [DECISIONS §409] ──────────────────────
 *
 * Le 404 n'arrive QUE si un morceau manque. Or le service worker précharge
 * `index.html` et le paquet principal, et répond aux navigations depuis ce
 * cache (`navigateFallback`) : **rien ne manque jamais**, donc l'évènement ne
 * part jamais, donc rien ne recharge. Et `registerSW.js` enregistre le service
 * worker une fois au `load` sans jamais le remettre à jour.
 *
 * Une application installée sur un téléphone — le manifeste la déclare
 * `standalone`, et on ne la ferme pas — peut donc servir un paquet vieux de
 * plusieurs semaines : sans 404, sans erreur, et sans que rien ne le dise. À
 * trois semaines du concours et plusieurs déploiements par jour, c'est un
 * candidat qui révise sur une application que personne ne peut plus corriger.
 *
 * D'où le second garde-fou : on DEMANDE au serveur quel paquet il sert, et on
 * recharge quand ce n'est plus celui qui tourne.
 */

/** La marque laissée par un rechargement déjà tenté, le temps de l'onglet. */
const DEJA_TENTE = 'haicademy:rechargement-de-version'

/** Toutes les dix minutes, et à chaque retour sur l'onglet. */
const INTERVALLE_MS = 10 * 60 * 1000

/** Recharge une fois, et pas deux — le même garde-fou que pour le 404. */
function rechargerUneFois(): void {
  if (sessionStorage.getItem(DEJA_TENTE) !== null) return

  sessionStorage.setItem(DEJA_TENTE, '1')
  window.location.reload()
}

/**
 * Demande au serveur quel paquet il sert, et recharge si ce n'est plus le nôtre.
 *
 * **La chaîne de requête n'est pas décorative** : le préchargement de Workbox
 * indexe l'URL EXACTE. Sans elle, la requête serait servie depuis le cache du
 * service worker — c'est-à-dire par la version périmée elle-même, qui se
 * répondrait qu'elle est à jour. `cache: 'no-store'` écarte en plus le cache
 * HTTP du navigateur.
 *
 * **Un échec ne recharge rien.** Un candidat haïtien perd la connexion souvent ;
 * le sortir de sa séance pour une requête tombée lui coûterait sa place, et
 * hors ligne la page revenue serait de toute façon la même.
 *
 * @param enCours l'adresse du paquet qui tourne — `import.meta.url` en vrai.
 */
export async function verifierLaVersion(enCours: string): Promise<void> {
  let servie: string

  try {
    const reponse = await fetch(`/index.html?v=${String(Date.now())}`, { cache: 'no-store' })

    if (!reponse.ok) return

    servie = await reponse.text()
  } catch {
    return
  }

  const paquet = /\/assets\/(index-[A-Za-z0-9_-]+\.js)/.exec(servie)?.[1]

  if (paquet === undefined || enCours.includes(paquet)) return

  /*
   * **Le service worker d'abord, le rechargement ensuite.** Il répond aux
   * navigations depuis son `index.html` préchargé : recharger sans le mettre à
   * jour rendrait la MÊME page périmée, et le garde-fou anti-boucle bloquerait
   * la seconde tentative — le candidat resterait coincé pour de bon.
   *
   * `update()` va chercher `sw.js` ; s'il a changé, `registerType: 'autoUpdate'`
   * l'installe, le fait prendre la main sans attendre, et le rechargement qui
   * suit reçoit le paquet neuf. Un échec ici ne doit rien empêcher : mieux vaut
   * recharger sans avoir pu mettre à jour que ne pas recharger du tout.
   */
  try {
    const enregistrement = await navigator.serviceWorker?.getRegistration()

    await enregistrement?.update()
  } catch {
    // Navigateur sans service worker, onglet privé, mise à jour refusée : on
    // recharge quand même.
  }

  rechargerUneFois()
}

/**
 * Recharge la page une fois quand un morceau du paquet a disparu.
 *
 * `vite:preloadError` est l'évènement que Vite émet précisément pour ce cas :
 * il porte l'échec du chargement d'un module, avant que la promesse ne remonte
 * jusqu'à React sous une forme méconnaissable.
 */
export function surveillerLaVersionServie(): void {
  window.addEventListener('vite:preloadError', (evenement) => {
    // Sans cela, Vite laisse l'erreur remonter et l'écran reste vide.
    evenement.preventDefault()

    rechargerUneFois()
  })

  /*
   * **Le drapeau se lève au chargement suivant, pas au rechargement.** Posé
   * juste avant `reload()`, il doit survivre à celui-ci pour empêcher la
   * boucle ; une fois la page revenue et tenue une poignée de secondes, la
   * version servie est la bonne et le drapeau n'a plus lieu d'être. Le laisser
   * ferait rater le PROCHAIN déploiement dans le même onglet.
   */
  window.setTimeout(() => { sessionStorage.removeItem(DEJA_TENTE) }, 10_000)

  /*
   * **Et l'on va voir de soi-même.** Au retour sur l'onglet d'abord — c'est le
   * moment où le candidat reprend, et celui où une page rechargée coûte le
   * moins —, puis toutes les dix minutes pour l'onglet qu'on ne quitte pas.
   */
  const verifier = (): void => { void verifierLaVersion(import.meta.url) }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') verifier()
  })

  window.setInterval(verifier, INTERVALLE_MS)
}
